/**
 * Shared helpers for report forms.
 */
import * as React from 'react';
import {
  REPORT_IN_PERSON_SESSION_TYPE as SESSION_TYPE,
  STUDENT_ABSENCE_REASONS,
  TUTOR_ABSENCE_REASONS,
} from '@/components/reports/report-configs';
import type { AbsenceReason, ParticipantAttendance } from '@/types/attendance.types';
import type {
  CreateReportDto,
  CreateReportFormState,
  SessionReport,
  SessionReportAttendanceStatus,
  SessionReportFeeling,
  StudentFeedbackEdit,
  TutorFeedbackValue,
  UpdateReportDto,
} from '@/types/inperson/report.types';
import type { SessionParticipant } from '@/types/inperson/session.types';
import { normalizeAuthorFeedbacks } from '@/utils/report-author-feedback-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { roleKind } from '@/utils/role-label-utils';

/**
 * Statuses offered on the in-person report attendance control.
 * Stored `excused` and `pending` stay unchanged; they are not choices.
 */
export const INPERSON_REPORT_OFFERED_ATTENDANCE_STATUSES = ['present', 'absent'] as const;

/** Participant role used when picking absence-reason catalogs. */
type AttendanceRole = SessionParticipant['role'];

/** Author identity used on create (id + display fallbacks). */
export type ReportFormAuthor = {
  id: string;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

/** Field-level validation callbacks used while creating a report. */
type CreateReportFormValidation = {
  validateField: (field: string, value: unknown, formData?: unknown) => unknown;
  markAsTouched: (field: string) => void;
  markAsDirty: (field: string) => void;
};

/** Minimal pair row used to tell paired VSC from floating observers. */
type StudentTutorPairRef = {
  tutorUserId: string;
};

/** Join subject ids into the comma-separated payload the API expects. */
function serializeReportSubjectFromIds(ids: string[]): string {
  return ids.filter(Boolean).join(',');
}

/** True when the report belongs to an information meeting. */
export function isInformationMeetingReport(sessionType?: string): boolean {
  return sessionType === SESSION_TYPE.informationMeeting;
}

/** Catalog label for an absence reason (tutor vs student catalogs). */
export function buildAbsenceJustification(
  role: AttendanceRole,
  reason?: AbsenceReason,
): string | null {
  if (!reason) return null;
  const kind = roleKind(role);
  const isTutorOrVolunteer =
    kind === 'tutor' || kind === 'volunteer' || kind === 'coordinator' || kind === 'recruiter';
  const reasonLabels = isTutorOrVolunteer ? TUTOR_ABSENCE_REASONS : STUDENT_ABSENCE_REASONS;
  return reasonLabels[reason as keyof typeof reasonLabels];
}

/** Reverse a stored justification label back to reason + comment. */
function parseStoredAbsence(
  role: AttendanceRole,
  absenceJustification: string | null | undefined,
  absenceComment: string | null | undefined,
): Pick<ParticipantAttendance, 'absenceReason' | 'absenceComment'> {
  if (!absenceJustification?.trim()) return {};
  const isTutorOrVolunteer = role === 'tutor' || role === 'volunteer';
  const reasonLabels = isTutorOrVolunteer ? TUTOR_ABSENCE_REASONS : STUDENT_ABSENCE_REASONS;
  for (const [key, label] of Object.entries(reasonLabels) as [AbsenceReason, string][]) {
    if (absenceJustification === label) {
      return {
        absenceReason: key,
        absenceComment: absenceComment?.trim() || undefined,
      };
    }
    if (absenceJustification.startsWith(`${label}: `)) {
      return {
        absenceReason: key,
        absenceComment:
          absenceComment?.trim() ||
          absenceJustification.slice(label.length + 2).trim() ||
          undefined,
      };
    }
  }
  return {};
}

/** Sort participants tutor → student → volunteer. */
export function sortAttendanceParticipants(
  participants: SessionParticipant[],
): SessionParticipant[] {
  const order: Record<string, number> = { tutor: 0, student: 1, volunteer: 2 };
  return [...participants].sort((a, b) => (order[a.role] ?? 3) - (order[b.role] ?? 3));
}

/** Seed the attendance map from stored statuses (default `absent`). */
export function initAttendanceMap(
  participants: SessionParticipant[],
): Map<string, ParticipantAttendance> {
  const initial = new Map<string, ParticipantAttendance>();
  for (const participant of participants) {
    const status = (participant.attendanceStatus ||
      'absent') as ParticipantAttendance['attendanceStatus'];
    initial.set(participant.id, {
      participantId: participant.id,
      userId: participant.userId,
      role: participant.role,
      attendanceStatus: status,
      ...parseStoredAbsence(
        participant.role,
        participant.absenceJustification,
        participant.absenceComment,
      ),
    });
  }
  return initial;
}

/** Attendance row for a participant, falling back to stored session values. */
export function attendanceValueFor(
  participant: SessionParticipant,
  attendanceData: Map<string, ParticipantAttendance>,
): ParticipantAttendance {
  return (
    attendanceData.get(participant.id) ?? {
      participantId: participant.id,
      userId: participant.userId,
      role: participant.role,
      attendanceStatus: (participant.attendanceStatus ||
        'absent') as ParticipantAttendance['attendanceStatus'],
      ...parseStoredAbsence(
        participant.role,
        participant.absenceJustification,
        participant.absenceComment,
      ),
    }
  );
}

/** Immutable set of one attendance map entry. */
export function setAttendanceEntry(
  prev: Map<string, ParticipantAttendance>,
  participantId: string,
  next: ParticipantAttendance,
): Map<string, ParticipantAttendance> {
  const nextMap = new Map(prev);
  nextMap.set(participantId, next);
  return nextMap;
}

/** Absence-reason / other-comment errors keyed by participant id. */
export function collectAttendanceErrors(
  attendanceData: Map<string, ParticipantAttendance>,
): Map<string, string> {
  const errors = new Map<string, string>();
  attendanceData.forEach((value, participantId) => {
    if (value.attendanceStatus !== 'absent') return;
    if (!value.absenceReason) {
      errors.set(participantId, getErrorMessage('absenceReasonRequired'));
    } else if (value.absenceReason === 'other' && !value.absenceComment?.trim()) {
      errors.set(participantId, getErrorMessage('specifyReason'));
    }
  });
  return errors;
}

/** True when every absent participant has a valid justification. */
export function isAttendanceMapValid(
  attendanceData: Map<string, ParticipantAttendance>,
  participantCount: number,
): boolean {
  if (participantCount === 0) return true;
  return collectAttendanceErrors(attendanceData).size === 0;
}

/** PATCH attendance rows; justification/comment only when absent. */
export function buildAttendancePayload(
  participants: SessionParticipant[],
  attendanceData: Map<string, ParticipantAttendance>,
): NonNullable<UpdateReportDto['participants']> {
  return participants.map((p) => {
    const att = attendanceData.get(p.id);
    const status = (att?.attendanceStatus ??
      p.attendanceStatus ??
      'absent') as SessionReportAttendanceStatus;
    return {
      participantId: p.id,
      attendanceStatus: status,
      absenceJustification:
        status === 'absent' ? buildAbsenceJustification(p.role, att?.absenceReason) : null,
      absenceComment: status === 'absent' ? att?.absenceComment?.trim() || null : null,
    };
  });
}

/** Serialized subject string for live / submit validation. */
export function subjectValueForEditValidation(subjectIds: string[]): string {
  return serializeReportSubjectFromIds(subjectIds);
}

/** Subject options: session list when `sessionSubjectIds` is set, otherwise full catalog. */
export function resolveReportSubjectOptions(
  sessionSubjectIds: string[] | undefined,
  catalog: { id: string; label: string }[],
): { value: string; label: string }[] {
  if (!catalog.length) return [];
  const ids = sessionSubjectIds?.filter(Boolean) ?? [];
  if (ids.length > 0) {
    const idSet = new Set(ids);
    return catalog.filter((s) => idSet.has(s.id)).map((s) => ({ value: s.id, label: s.label }));
  }
  return catalog.map((s) => ({ value: s.id, label: s.label }));
}

/** Empty create form (rating/feeling unset). */
export function emptyCreateReportForm(): CreateReportFormState {
  return { rating: null, subjectIds: [], comment: '', feeling: null };
}

/** Validator payload: serialize subject ids to a single string. */
export function createReportValidationPayload(form: CreateReportFormState): {
  subject: string;
  rating: number | null;
  comment: string;
} {
  return {
    subject: serializeReportSubjectFromIds(form.subjectIds),
    rating: form.rating,
    comment: form.comment,
  };
}

/** Prefill subject ids from the session unless the user already touched them. */
export function prefillSubjectIds(
  prevIds: string[],
  sessionSubjectIds: string[] | undefined,
  touched: boolean,
): string[] {
  if (touched || !sessionSubjectIds?.length || prevIds.length > 0) return prevIds;
  return [...sessionSubjectIds];
}

/** `displayName` → first+last → email → fallback. */
export function resolveReportAuthorName(user: ReportFormAuthor): string {
  return (
    user.displayName ||
    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
    user.email ||
    'Auteur inconnu'
  );
}

/** Build create-report DTO from author + form. */
export function buildCreateReportDto(
  user: ReportFormAuthor,
  form: CreateReportFormState,
): CreateReportDto {
  return {
    authorId: user.id,
    authorFeedbacks: [
      {
        idAuteur: user.id,
        rating: form.rating,
        comment: form.comment.trim() || null,
        feeling: form.feeling,
      },
    ],
    subjectIds: form.subjectIds.length > 0 ? form.subjectIds : undefined,
    authorName: resolveReportAuthorName(user),
  };
}

/** Change / blur handlers that keep validation in sync. */
export function useCreateReportFormHandlers(
  setFormData: React.Dispatch<React.SetStateAction<CreateReportFormState>>,
  validation: CreateReportFormValidation,
  subjectIdsTouchedByUser: React.RefObject<boolean>,
) {
  const handleFieldChange = (
    field: Exclude<keyof CreateReportFormState, 'subjectIds'>,
    value: CreateReportFormState[typeof field],
  ) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      const payload = createReportValidationPayload(next);
      if (field !== 'feeling') {
        validation.validateField(field, value, payload as never);
        validation.markAsTouched(field);
        validation.markAsDirty(field);
      }
      return next;
    });
  };

  const handleSubjectIdsChange = (values: string[]) => {
    subjectIdsTouchedByUser.current = true;
    setFormData((prev) => {
      const next = { ...prev, subjectIds: values };
      const payload = createReportValidationPayload(next);
      validation.validateField('subject', payload.subject, payload as never);
      validation.markAsTouched('subject');
      validation.markAsDirty('subject');
      return next;
    });
  };

  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  return { handleFieldChange, handleSubjectIdsChange, handleBlur };
}

/** Central error copy from {@link getErrorMessage}. */
export function getReportApiErrorMessage(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}

/**
 * External VSC: volunteer not assigned any student (floating observer).
 * Paired VSC (`tutorUserId` in studentTutorPairs): treated like a tutor → attendance.
 */
export function partitionParticipantsForReport(
  participants: SessionParticipant[] | undefined,
  pairs?: StudentTutorPairRef[] | null,
): {
  vscParticipants: SessionParticipant[];
  attendanceParticipants: SessionParticipant[];
} {
  const list = participants ?? [];
  const pairedFacilitatorIds = new Set((pairs ?? []).map((p) => p.tutorUserId));
  const isExternalVsc = (p: SessionParticipant) =>
    p.role === 'volunteer' && !pairedFacilitatorIds.has(p.userId);

  return {
    vscParticipants: list.filter(isExternalVsc),
    attendanceParticipants: list.filter((p) => !isExternalVsc(p)),
  };
}

/** Session participant whose linked user was soft-deleted (API returns user: null). */
export function isArchivedParticipant(participant: SessionParticipant): boolean {
  return participant.user == null;
}

/** Attendance rows whose linked user is still present (not archived). */
export function getEditableAttendanceParticipants(
  participants: SessionParticipant[],
): SessionParticipant[] {
  return participants.filter((participant) => !isArchivedParticipant(participant));
}

/** Students among already-editable attendance participants. */
export function getStudentParticipants(participants: SessionParticipant[]): SessionParticipant[] {
  return participants.filter((participant) => participant.role === 'student');
}

/** Flatten catalog categories into `{ value, label }` select options. */
export function mapStudentFeedbackCatalogOptions(
  catalog: Array<{ items: Array<{ id: string; label: string }> }> | undefined,
): Array<{ value: string; label: string }> {
  return (catalog ?? []).flatMap((category) =>
    category.items.map((item) => ({ value: item.id, label: item.label })),
  );
}

/** Author ids the current user may edit (self, or all when privileged). */
export function resolveEditableAuthorIds(
  hasReport: boolean,
  userId: string | undefined,
  privileged: boolean,
  sectionAuthorIds: string[],
): Set<string> {
  if (!hasReport || !userId) return new Set();
  if (privileged) return new Set(sectionAuthorIds);
  return new Set([userId]);
}

/** Hydrate tutoring edit: author feedback, subjects, student feedback. */
export function hydrateTutoringReportEditState(
  report: SessionReport,
  userId: string | undefined,
  studentParticipants: Array<{ userId: string }> = [],
): {
  feedbackByAuthor: Map<string, TutorFeedbackValue>;
  subjectIds: string[];
  initialValidation: {
    rating: number | null;
    subject: string;
    comment: string;
    feeling: SessionReportFeeling | null;
  };
  studentFeedbacks: Map<string, StudentFeedbackEdit>;
} {
  const feedbacks = normalizeAuthorFeedbacks(report);
  const feedbackByAuthor = new Map<string, TutorFeedbackValue>();
  for (const feedback of feedbacks) {
    feedbackByAuthor.set(feedback.idAuteur, {
      rating: feedback.rating,
      comment: feedback.comment ?? '',
      feeling: feedback.feeling ?? null,
    });
  }

  const actorFeedback = feedbacks.find((feedback) => feedback.idAuteur === userId) ?? feedbacks[0];

  const studentFeedbacks = new Map<string, StudentFeedbackEdit>();
  for (const participant of studentParticipants) {
    const existing = (report.studentFeedbacks ?? []).find(
      (feedback) => feedback.participantUserId === participant.userId,
    );
    studentFeedbacks.set(participant.userId, {
      workedItemIds: existing?.workedItemIds ?? [],
      rating: existing?.rating ?? null,
      comment: existing?.comment ?? '',
    });
  }

  return {
    feedbackByAuthor,
    subjectIds: report.subjectIds?.length ? [...report.subjectIds] : [],
    initialValidation: {
      rating: actorFeedback?.rating ?? null,
      subject: serializeReportSubjectFromIds(report.subjectIds ?? []),
      comment: actorFeedback?.comment ?? '',
      feeling: actorFeedback?.feeling ?? null,
    },
    studentFeedbacks,
  };
}

/** Prefill subject ids from the session unless the user already touched them. */
export function resolveTutoringReportSubjectPrefill(
  report: SessionReport,
  sessionSubjectIds: string[] | undefined,
  currentSubjectIds: string[],
  touched: boolean,
): string[] | null {
  if (touched) return null;
  if (!sessionSubjectIds?.length) return null;
  if (report.subjectIds && report.subjectIds.length > 0) return null;
  if (currentSubjectIds.length > 0) return null;
  return [...sessionSubjectIds];
}

/** PATCH tutoring report: feedbacks, subjects, attendance. */
export function buildUpdateTutoringReportDto(params: {
  feedbackByAuthor: Map<string, TutorFeedbackValue>;
  canEditAllAuthors: boolean;
  editableAuthorIds: Set<string>;
  subjectIds: string[];
  attendanceParticipants: SessionParticipant[];
  attendanceData: Map<string, ParticipantAttendance>;
  studentParticipants: Array<{ userId: string }>;
  studentFeedbackData: Map<string, StudentFeedbackEdit>;
}): UpdateReportDto {
  const participantPayload = buildAttendancePayload(
    params.attendanceParticipants,
    params.attendanceData,
  );

  const authorFeedbacksPatch = [...params.feedbackByAuthor.entries()]
    .filter(([id]) => params.canEditAllAuthors || params.editableAuthorIds.has(id))
    .map(([idAuteur, value]) => ({
      idAuteur,
      rating: value.rating,
      comment: value.comment || null,
      feeling: value.feeling,
    }));

  const studentFeedbacksPayload = params.studentParticipants
    .map((participant) => {
      const feedback = params.studentFeedbackData.get(participant.userId);
      if (!feedback) return null;
      return {
        participantUserId: participant.userId,
        workedItemIds: feedback.workedItemIds,
        rating: feedback.rating,
        comment: feedback.comment.trim() || null,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return {
    authorFeedbacks: authorFeedbacksPatch.length > 0 ? authorFeedbacksPatch : undefined,
    ...(params.subjectIds.length > 0 ? { subjectIds: params.subjectIds } : {}),
    ...(participantPayload.length > 0 ? { participants: participantPayload } : {}),
    ...(studentFeedbacksPayload.length > 0 ? { studentFeedbacks: studentFeedbacksPayload } : {}),
  };
}

/** PATCH information-meeting report: synthesis, rating, attendance. */
export function buildUpdateInformationMeetingReportDto(params: {
  freeSynthesis: string;
  engagementLevel: number;
  attendanceParticipants: SessionParticipant[];
  attendanceData: Map<string, ParticipantAttendance>;
}): UpdateReportDto {
  const participantPayload = buildAttendancePayload(
    params.attendanceParticipants,
    params.attendanceData,
  );
  return {
    comment: params.freeSynthesis.trim(),
    rating: params.engagementLevel,
    ...(participantPayload.length > 0 ? { participants: participantPayload } : {}),
  };
}
